'use client'

import { useState } from 'react'
import { router } from '@inertiajs/react'
import { StarIcon } from '@heroicons/react/20/solid'
import { CheckCircleIcon } from '@heroicons/react/24/outline'

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function ReviewForm({ product }) {
    const [rating, setRating]       = useState(0)
    const [hovered, setHovered]     = useState(0)
    const [comment, setComment]     = useState('')
    const [errors, setErrors]       = useState({})
    const [submitting, setSubmitting] = useState(false)
    const [submitted, setSubmitted] = useState(false)

    const handleSubmit = (e) => {
        e.preventDefault()
        setErrors({})
        setSubmitting(true)

        router.post(route('reviews.store', { product: product.id }), {
            rating,
            comment,
        }, {
            preserveScroll: true,
            onSuccess: () => {
                setRating(0)
                setComment('')
                setSubmitted(true)
                setTimeout(() => setSubmitted(false), 4000)
            },
            onError: (errs) => setErrors(errs),
            onFinish: () => setSubmitting(false),
        })
    }

    if (submitted) {
        return (
            <div className="mt-16 flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-4">
                <CheckCircleIcon className="h-6 w-6 shrink-0 text-emerald-500" />
                <p className="text-sm font-medium text-emerald-800">Thank you! Your review has been submitted.</p>
            </div>
        )
    }

    return (
        <div className="mt-16">
            <h2 className="text-lg font-medium text-gray-900">Write a review</h2>
            <form onSubmit={handleSubmit} className="mt-6 space-y-6">
                {/* Star rating */}
                <div>
                    <label className="text-sm font-medium text-gray-900">Rating <span className="text-red-500">*</span></label>
                    <div className="mt-2 flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <StarIcon
                                key={star}
                                onClick={() => setRating(star)}
                                onMouseEnter={() => setHovered(star)}
                                onMouseLeave={() => setHovered(0)}
                                className={classNames(
                                    (hovered || rating) >= star ? 'text-yellow-400' : 'text-gray-300',
                                    'h-7 w-7 cursor-pointer transition-colors'
                                )}
                            />
                        ))}
                        {rating > 0 && (
                            <span className="ml-2 text-sm text-gray-500">{['', 'Poor', 'Fair', 'Good', 'Very good', 'Excellent'][rating]}</span>
                        )}
                    </div>
                    {errors.rating && <p className="mt-1 text-sm text-red-600">{errors.rating}</p>}
                </div>

                {/* Comment */}
                <div>
                    <label htmlFor="comment" className="text-sm font-medium text-gray-900">Comment <span className="text-red-500">*</span></label>
                    <textarea
                        id="comment"
                        name="comment"
                        rows={4}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Share your experience with this product…"
                        className="mt-2 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 shadow-sm focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-500"
                    />
                    {errors.comment && <p className="mt-1 text-sm text-red-600">{errors.comment}</p>}
                </div>

                <button
                    type="submit"
                    disabled={submitting || rating === 0}
                    className="flex w-full items-center justify-center gap-2 bg-[#222222] px-8 py-3 text-sm font-black uppercase tracking-widest text-white transition-colors hover:bg-[#F93A00] disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {submitting ? 'Submitting…' : 'Submit Review'}
                </button>
            </form>
        </div>
    )
}
